// Kinio plane api：設備 + shadow（docs/KINIO-API.md §2）。
//
// shadow key 是 canonical `<cluster>.<attr>`：開關 = `on_off.on`（bool）。
// 收斂（SPEC #shadow-sync）：POST desired 後每 500 ms GET shadow，直到該 key 不在 desired（設備 ack 後 gateway 清掉；
// 寫入值本來就等於 reported 時當下清掉）——不看 delta。收斂後一律以 reported 重畫；reported 跟寫入值不同（[adopted] false）
// = 設備正規化或拒收，UI 標「已依設備回報調整」。≤ 25 s；10 s 沒動可再 POST 一次，冪等。

import { kinioFetch } from './auth';
import { planeBase } from './config';
import { KinioError } from './errors';
import type { DesiredResponse, Shadow, ShadowState, ThingDetail } from './types';

function thingPath(apiEndpoint: string, homeId: string, aid: string): string {
  return `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(aid)}`;
}

/** GET …/things/{device} → thing + presence（online|offline）。 */
export function getThing(apiEndpoint: string, homeId: string, aid: string, signal?: AbortSignal): Promise<ThingDetail> {
  return kinioFetch<ThingDetail>(thingPath(apiEndpoint, homeId, aid), { signal });
}

/** 移除設備（owner / admin）：DELETE …/things/{device} → {unbound, device_reset?}（平台會先叫設備回原廠，SPEC #brand-app-ux 維護段）。 */
export function unbindThing(apiEndpoint: string, homeId: string, aid: string): Promise<{ unbound?: boolean; device_reset?: boolean } | undefined> {
  return kinioFetch<{ unbound?: boolean; device_reset?: boolean } | undefined>(thingPath(apiEndpoint, homeId, aid), { method: 'DELETE' });
}

/** GET …/things/{device}/shadow */
export function getShadow(apiEndpoint: string, homeId: string, aid: string, signal?: AbortSignal): Promise<Shadow> {
  return kinioFetch<Shadow>(thingPath(apiEndpoint, homeId, aid) + '/shadow', { signal });
}

/** POST …/things/{device}/shadow/desired `{state, client_token}` → accepted / delta / pushed_to_device。 */
export function setDesired(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  state: ShadowState,
  clientToken = 'app-web',
  signal?: AbortSignal,
): Promise<DesiredResponse> {
  return kinioFetch<DesiredResponse>(thingPath(apiEndpoint, homeId, aid) + '/shadow/desired', {
    method: 'POST',
    body: { state, client_token: clientToken },
    signal,
  });
}

/** 讀 shadow 的一個 canonical 屬性（reported 或 desired / delta）。 */
export function shadowAttr(state: ShadowState | undefined, cluster: string, attr: string): unknown {
  const c = state?.[cluster];
  return c && typeof c === 'object' ? (c as Record<string, unknown>)[attr] : undefined;
}

export const CONVERGE_INTERVAL_MS = 500;
export const CONVERGE_TIMEOUT_MS = 25_000;
export const CONVERGE_REPOST_MS = 10_000;

export interface ConvergeTarget {
  cluster: string;
  attr: string;
  value: unknown;
  /**
   * reported 跟目標值怎麼比（[adopted] 用；沒給 = `===`）。給了也當提早結束的條件：偵測區域編輯器的 settled 規則——
   * reported 解析後 == 送出的清單（或已跟寫入前不同）就以 reported 重畫，不等 desired 清掉。
   */
  equals?: (reported: unknown, value: unknown) => boolean;
}

export interface ConvergeOptions {
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** 每次輪詢拿到 shadow 時回呼（UI 顯示 reported / pending）。 */
  onShadow?: (s: Shadow) => void;
  /** 10 s 未收斂時呼叫一次（冪等重送 desired）。 */
  repost?: () => Promise<unknown>;
  repostAfterMs?: number;
  /** 測試注入。 */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/** 該 key 還在 desired（設備還沒套用 = Pending）。 */
export function desiredPending(s: Shadow, cluster: string, attr: string): boolean {
  return shadowAttr(s.state?.desired, cluster, attr) !== undefined;
}

/** 該 key 已不在 desired（設備 ack 了、或寫入值本來就是 reported）；有自訂 [ConvergeTarget.equals] 時它成立也算。值以 reported 為準。 */
export function isConverged(s: Shadow, target: ConvergeTarget): boolean {
  if (!desiredPending(s, target.cluster, target.attr)) return true;
  return target.equals ? target.equals(shadowAttr(s.state?.reported, target.cluster, target.attr), target.value) : false;
}

/** 收斂後設備實際採用的值是不是寫入值（不是 = 設備正規化或拒收 → UI 標「已依設備回報調整」）。 */
export function adopted(s: Shadow, target: ConvergeTarget): boolean {
  const reported = shadowAttr(s.state?.reported, target.cluster, target.attr);
  return target.equals ? target.equals(reported, target.value) : reported === target.value;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * 輪詢 shadow 直到收斂。回傳收斂時的 shadow；逾時拋 `KinioError(408 CONVERGE_TIMEOUT)`；
 * `signal` 中止拋 `KinioError(0 ABORTED)`。
 */
export async function converge(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  target: ConvergeTarget,
  opts: ConvergeOptions = {},
): Promise<Shadow> {
  const interval = opts.intervalMs ?? CONVERGE_INTERVAL_MS;
  const timeout = opts.timeoutMs ?? CONVERGE_TIMEOUT_MS;
  const repostAfter = opts.repostAfterMs ?? CONVERGE_REPOST_MS;
  const now = opts.now ?? (() => Date.now());
  const sleep = opts.sleep ?? defaultSleep;
  const started = now();
  let reposted = false;
  for (;;) {
    if (opts.signal?.aborted) throw new KinioError(0, 'ABORTED', 'converge aborted');
    const shadow = await getShadow(apiEndpoint, homeId, aid, opts.signal);
    opts.onShadow?.(shadow);
    if (isConverged(shadow, target)) return shadow;
    const elapsed = now() - started;
    if (elapsed >= timeout) throw new KinioError(408, 'CONVERGE_TIMEOUT', `shadow ${target.cluster}.${target.attr} did not converge in ${timeout}ms`);
    if (!reposted && opts.repost && elapsed >= repostAfter) {
      reposted = true;
      try {
        await opts.repost();
      } catch {
        /* 重送失敗不中斷輪詢：下一輪可能已收斂 */
      }
    }
    await sleep(interval);
  }
}

export interface SetOnOffResult {
  desired: DesiredResponse;
  shadow: Shadow;
}

/**
 * P0 開關：desired `{on_off:{on}}` + 收斂。`pushed_to_device=false`（設備離線）仍會輪詢
 * 至逾時——呼叫端可先看 presence 決定要不要送。
 */
export async function setOnOff(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  on: boolean,
  opts: Omit<ConvergeOptions, 'repost'> & { clientToken?: string } = {},
): Promise<SetOnOffResult> {
  const post = () => setDesired(apiEndpoint, homeId, aid, { on_off: { on } }, opts.clientToken, opts.signal);
  const desired = await post();
  const shadow = await converge(apiEndpoint, homeId, aid, { cluster: 'on_off', attr: 'on', value: on }, { ...opts, repost: post });
  return { desired, shadow };
}
